import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminProfileAnalyticsService } from '../../../services/profileAnalyticsService';
import { playerProfileService } from '../../../services/playerMasterService';
import { supabase } from '../../../lib/supabase';

const AdminVisitorAnalyticsManager = ({ onClose }) => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [formData, setFormData] = useState({
    visitorName: '',
    visitorType: 'club',
    visitorOrganization: '',
    visitorCountry: '',
    visitorLeague: '',
    visitCount: 1
  });

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    if (selectedPlayerId) {
      fetchVisitors();
    }
  }, [selectedPlayerId]);

  const fetchPlayers = async () => {
    try {
      const response = await playerProfileService?.getAllForAdmin();
      const playerList = response?.data || [];
      if (playerList?.length === 0) {
        // Fallback: direct query in case service has issues
        const { data, error } = await supabase?.from('player_master_profiles')?.select('id, full_name, primary_position, current_club')?.order('created_at', { ascending: false });
        if (error) {
          console.error('Fallback player fetch error:', error);
        } else {
          setPlayers(
            (data || [])?.map((p) => ({
              id: p?.id,
              fullName: p?.full_name,
              primaryPosition: p?.primary_position,
              currentClub: p?.current_club
            }))
          );
          return;
        }
      }
      setPlayers(playerList);
    } catch (error) {
      console.error('Error fetching players:', error);
      // Fallback: direct query
      try {
        const { data, error: fallbackError } = await supabase?.from('player_master_profiles')?.select('id, full_name, primary_position, current_club')?.order('created_at', { ascending: false });
        if (fallbackError) {
          console.error('Fallback player fetch error:', fallbackError);
        } else {
          setPlayers(
            (data || [])?.map((p) => ({
              id: p?.id,
              fullName: p?.full_name,
              primaryPosition: p?.primary_position,
              currentClub: p?.current_club
            }))
          );
        }
      } catch (fallbackErr) {
        console.error('All player fetch attempts failed:', fallbackErr);
      }
    }
  };

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const response = await adminProfileAnalyticsService?.getAllVisitors({ playerId: selectedPlayerId });
      setVisitors(response?.data || []);
    } catch (error) {
      console.error('Error fetching visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      visitorName: '',
      visitorType: 'club',
      visitorOrganization: '',
      visitorCountry: '',
      visitorLeague: '',
      visitCount: 1
    });
    setEditingVisitor(null);
    setShowAddForm(false);
  };

  const handleEditVisitor = (visitor) => {
    setEditingVisitor(visitor);
    setFormData({
      visitorName: visitor?.visitorName || '',
      visitorType: visitor?.visitorType || 'club',
      visitorOrganization: visitor?.visitorOrganization || '',
      visitorCountry: visitor?.visitorCountry || '',
      visitorLeague: visitor?.visitorLeague || '',
      visitCount: visitor?.visitCount || 1
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!selectedPlayerId) {
      alert('Please select a player first');
      return;
    }

    try {
      if (editingVisitor) {
        await adminProfileAnalyticsService?.updateVisitor(editingVisitor?.id, {
          visitorOrganization: formData?.visitorOrganization,
          visitorCountry: formData?.visitorCountry,
          visitorLeague: formData?.visitorLeague,
          visitCount: formData?.visitCount
        });
        alert('Visitor updated successfully!');
      } else {
        await adminProfileAnalyticsService?.upsertVisitor({
          playerId: selectedPlayerId,
          ...formData
        });
        alert('Visitor added successfully!');
      }
      resetForm();
      fetchVisitors();
    } catch (error) {
      console.error('Error saving visitor:', error);
      alert('Failed to save visitor: ' + error?.message);
    }
  };

  const handleDeleteVisitor = async (visitorId) => {
    if (!confirm('Are you sure you want to delete this visitor?')) return;

    try {
      await adminProfileAnalyticsService?.deleteVisitor(visitorId);
      alert('Visitor deleted successfully!');
      fetchVisitors();
    } catch (error) {
      console.error('Error deleting visitor:', error);
      alert('Failed to delete visitor: ' + error?.message);
    }
  };

  const visitorTypes = [
    { value: 'club', label: 'Club' },
    { value: 'scout', label: 'Scout' },
    { value: 'sports_director', label: 'Sports Director' },
    { value: 'fifa_agent', label: 'FIFA Agent' },
    { value: 'club_scouter', label: 'Club Scouter' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-headline">Profile Visitor Analytics</h2>
            <p className="text-sm text-muted-foreground mt-1">Add, edit, and manage which clubs, agents, and scouts viewed a player's profile</p>
          </div>
          <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Player Selection */}
          <div className="bg-muted/50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-foreground mb-2">Select Player</label>
            <Select
              value={selectedPlayerId}
              onChange={(val) => setSelectedPlayerId(val)}
              placeholder="Choose a player..."
              options={players?.map((player) => ({
                value: player?.id,
                label: `${player?.fullName} - ${player?.primaryPosition} (${player?.currentClub || 'Free Agent'})`
              }))}
              className="w-full"
            />
          </div>

          {selectedPlayerId && (
            <>
              {/* Add / Edit Visitor Button */}
              <div className="flex justify-end">
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                  }}
                >
                  Add Visitor
                </Button>
              </div>

              {/* Add / Edit Visitor Form */}
              {showAddForm && (
                <form onSubmit={handleSubmit} className="bg-muted/50 rounded-lg p-6 space-y-4 border border-border">
                  <h3 className="text-lg font-bold text-foreground font-headline">
                    {editingVisitor ? 'Edit Visitor' : 'Add New Visitor'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Visitor Name *</label>
                      <Input
                        type="text"
                        value={formData?.visitorName}
                        onChange={(e) => setFormData({ ...formData, visitorName: e?.target?.value })}
                        required
                        disabled={!!editingVisitor}
                        placeholder="e.g., AC Milan, John Smith"
                      />
                      {editingVisitor && (
                        <p className="text-xs text-muted-foreground mt-1">Name cannot be changed when editing</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Visitor Type *</label>
                      <Select
                        value={formData?.visitorType}
                        onChange={(val) => setFormData({ ...formData, visitorType: val })}
                        options={visitorTypes}
                        disabled={!!editingVisitor}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Organization / Club Name</label>
                      <Input
                        type="text"
                        value={formData?.visitorOrganization}
                        onChange={(e) => setFormData({ ...formData, visitorOrganization: e?.target?.value })}
                        placeholder="e.g., AC Milan, FIFA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Country</label>
                      <Input
                        type="text"
                        value={formData?.visitorCountry}
                        onChange={(e) => setFormData({ ...formData, visitorCountry: e?.target?.value })}
                        placeholder="e.g., Italy, England"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">League</label>
                      <Input
                        type="text"
                        value={formData?.visitorLeague}
                        onChange={(e) => setFormData({ ...formData, visitorLeague: e?.target?.value })}
                        placeholder="e.g., Serie A, Premier League"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Visit Count</label>
                      <Input
                        type="number"
                        min="1"
                        value={formData?.visitCount}
                        onChange={(e) => setFormData({ ...formData, visitCount: parseInt(e?.target?.value) || 1 })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="default">
                      {editingVisitor ? 'Save Changes' : 'Add Visitor'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Visitors List */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-lg font-bold text-foreground font-headline">Visitors ({visitors?.length})</h3>
                  <p className="text-sm text-muted-foreground">Clubs, agents, scouts who viewed this player's profile</p>
                </div>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : visitors?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No visitors yet. Add clubs, agents or scouts who viewed this player.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Visitor Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Organization / Club</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Country</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">League</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Visits</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Last Visit</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {visitors?.map((visitor) => (
                          <tr key={visitor?.id} className="hover:bg-muted/50">
                            <td className="px-4 py-3 text-sm text-foreground font-medium">{visitor?.visitorName}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                visitor?.visitorType === 'club' ? 'bg-blue-100 text-blue-700' :
                                visitor?.visitorType === 'scout' ? 'bg-green-100 text-green-700' :
                                visitor?.visitorType === 'fifa_agent' ? 'bg-purple-100 text-purple-700' :
                                visitor?.visitorType === 'sports_director'? 'bg-orange-100 text-orange-700' : 'bg-muted text-muted-foreground'
                              }`}>
                                {visitor?.visitorType?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{visitor?.visitorOrganization || '-'}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{visitor?.visitorCountry || '-'}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{visitor?.visitorLeague || '-'}</td>
                            <td className="px-4 py-3 text-sm text-foreground font-semibold">{visitor?.visitCount}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {visitor?.lastVisitAt ? new Date(visitor?.lastVisitAt)?.toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  iconName="Pencil"
                                  onClick={() => handleEditVisitor(visitor)}
                                  className="text-primary hover:text-primary"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  iconName="Trash2"
                                  onClick={() => handleDeleteVisitor(visitor?.id)}
                                  className="text-error hover:text-error"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVisitorAnalyticsManager;